import { regionKey, type MacroWalkResponsePayload } from '@playcastdotio/Messaging';
import type {
  MacroStepCaptureResult,
  MacroStepCaptureSpec,
} from '../../../services/playjector/interfaces/IPlayjectorService';
import type { MacroGuestFrame } from '../../game-launch/managers/MacroStepRunner';

export type GuestFrameResolution =
  | { kind: 'resolved'; frame: MacroGuestFrame }
  | { kind: 'unavailable'; reason: 'no-capture' | 'no-cache' | 'invalid-dimensions' };

/**
 * Frame absence is a named outcome, not `undefined`. The N2 defect began with
 * resolveGuestFrame returning undefined on a leaf node, or a first node whose
 * children carry no expectedState, and callers treating that as skippable.
 */
export class GuestFrameTracker {
  private lastGuestFrame: MacroGuestFrame | null = null;

  public clear(): void {
    this.lastGuestFrame = null;
  }

  public rememberGuestFrame(width: number, height: number): void {
    if (width > 0 && height > 0) {
      this.lastGuestFrame = { width, height };
    }
  }

  public rememberGuestFrameFromHashes(hashes: Record<string, MacroStepCaptureResult>): void {
    const first = Object.values(hashes)[0];
    if (first) {
      this.rememberGuestFrame(first.width, first.height);
    }
  }

  public resolve(payload: MacroWalkResponsePayload): GuestFrameResolution {
    const first = payload.captures[0];
    if (first) {
      if (first.width > 0 && first.height > 0) {
        this.rememberGuestFrame(first.width, first.height);
        return { kind: 'resolved', frame: { width: first.width, height: first.height } };
      }
      return { kind: 'unavailable', reason: 'invalid-dimensions' };
    }

    if (this.lastGuestFrame) {
      return { kind: 'resolved', frame: this.lastGuestFrame };
    }

    return { kind: 'unavailable', reason: 'no-cache' };
  }

  /** Returns the frame for macro playback, or undefined when unavailable. */
  public frameForPlayback(payload: MacroWalkResponsePayload): MacroGuestFrame | undefined {
    const resolution = this.resolve(payload);
    return resolution.kind === 'resolved' ? resolution.frame : undefined;
  }

  public buildCaptureSpecs(payload: MacroWalkResponsePayload): Record<string, MacroStepCaptureSpec> {
    return Object.fromEntries(
      payload.captures.map((spec) => [
        spec.captureId,
        regionKey(spec.region) !== ''
          ? { width: spec.width, height: spec.height, region: spec.region }
          : { width: spec.width, height: spec.height },
      ]),
    );
  }
}
